import type { Page, Track } from "@segment/facade";

const PROXY_GTM_URL =
  "https://has.mws.com/d8ekrsultsef.js?c3gq6=DwFOMzg4XjktVyMyPDc0VBBYQVpdUxccWwgEB0IWHB8%3D";

const push = (...args: any[]) => {
  if (!window) return;

  if (!window.dataLayer) {
    (window as any).dataLayer = [];
  }

  window.dataLayer.push(...args);
};

/**
 * Create and get a custom GTM integration to use a same-site proxy url for loading GTM script
 *
 * This was copied over from the `@segment/analytics.js-integration-google-tag-manager` package and modified
 * See original source code: https://github.com/segmentio/analytics.js-integrations/blob/master/integrations/google-tag-manager/lib/index.js
 */
export const tryGetCustomGTMIntegration = async () => {
  try {
    // importing dynamically to avoid undefined `window` reference errors during compilation
    // it also helps to reduce the bundle size as this is only needed while Segment is being loaded
    const baseIntegration = (await import("@segment/analytics.js-integration"))
      .default;

    const GTM = baseIntegration("Google Tag Manager")
      .global("dataLayer")
      .global("google_tag_manager")
      .option("containerId", "")
      .option("environment", "")
      .option("trackNamedPages", true)
      .option("trackCategorizedPages", true)
      .tag("no-env", `<script src="${PROXY_GTM_URL}"></script>`);

    /**
     * Initialize.
     *
     * https://developers.google.com/tag-manager
     *
     */
    GTM.prototype.initialize = function () {
      push({ "gtm.start": Number(new Date()), event: "gtm.js" });

      this.load("no-env", this.options, this.ready);
    };

    GTM.prototype.loaded = function () {
      return !!(
        window?.dataLayer && Array.prototype.push !== window?.dataLayer.push
      );
    };

    GTM.prototype.page = function (page: Page) {
      const category = page.category();
      const name = page.fullName();
      const opts = this.options;

      // all
      if (opts.trackAllPages) {
        this.track(page.track());
      }

      // categorized
      if (
        category &&
        opts.trackCategorizedPages &&
        typeof category === "string"
      ) {
        this.track(page.track(category));
      }

      // named
      if (name && opts.trackNamedPages && typeof name === "string") {
        this.track(page.track(name));
      }
    };

    /**
     * Track.
     *
     * https://developers.google.com/tag-manager/devguide#events
     *
     */
    GTM.prototype.track = function (track: Track) {
      const props = track.properties();
      const userId = this.analytics.user().id();
      const anonymousId = this.analytics.user().anonymousId();
      if (userId) props.userId = userId;

      if (anonymousId) props.segmentAnonymousId = anonymousId;
      props.event = track.event();

      push(props);
    };

    return GTM;
  } catch {
    // fail silently
    return undefined;
  }
};
